import { useCallback, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { GLASS, TYPE } from "@/lib/glass";
import { WindowFilm } from "@/components/home/WindowFilm";
import { useSamplesFromPrice, openSamples } from "@/lib/samples";

const door = "group inline-flex items-center gap-2 py-2 font-mono-spec text-[0.56rem] uppercase tracking-[0.28em] text-[#F5F5F7]/55 transition-colors duration-500 hover:text-[#84DFF4]";
const doorWord = "border-b border-[#F5F5F7]/20 pb-px transition-colors duration-500 group-hover:border-[#84DFF4]/60";
const doorArrow = "transition-transform duration-500 group-hover:translate-x-0.5";

export default function WindowMoment() {
  const [playing, setPlaying] = useState(false);
  const samplesFrom = useSamplesFromPrice();
  const handlePlaying = useCallback(() => setPlaying(true), []);
  return (
    <section data-testid="window-moment" aria-labelledby="window-moment-line" className="relative isolate h-[100svh] min-h-[600px] w-full overflow-hidden bg-[#030305]">
      {/* Portrait footage stays portrait: full-bleed on phones, a standing sheet in the void on wide screens. */}
      <div className="absolute inset-0 lg:left-1/2 lg:right-0">
        <WindowFilm onPlaying={handlePlaying} />
      </div>
      <div aria-hidden="true" className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,transparent_30%,rgba(0,0,0,0.78)_100%)] lg:bg-[linear-gradient(90deg,#030305_0%,#030305_42%,rgba(3,3,5,0.15)_58%,rgba(3,3,5,0.15)_92%,#030305_100%)]" />
      <div aria-hidden="true" className="absolute inset-0 bg-[linear-gradient(180deg,rgba(0,0,0,0.38)_0%,rgba(0,0,0,0.06)_30%,rgba(0,0,0,0.06)_52%,rgba(0,0,0,0.5)_100%)] lg:hidden" />
      <div aria-hidden="true" className="absolute inset-x-0 bottom-0 h-[58%] bg-gradient-to-t from-[#030305] via-[#030305]/70 to-transparent lg:h-40 lg:via-transparent" />

      <div className="relative z-10 flex h-full flex-col px-6 pb-10 pt-28 md:px-12 lg:px-20">
        <div className="mt-auto w-full lg:my-auto lg:w-[50%]">
          <motion.p data-testid="window-moment-eyebrow"
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 1.2, delay: 0.8, ease: GLASS.settle }}
            className="type-eyebrow mb-5">
            PrismPixel<sup aria-hidden="true" className="ml-0.5 text-[0.7em]">™</sup> · Engineered optical materials
          </motion.p>
          <motion.h1 id="window-moment-line" data-testid="window-moment-line"
            initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 1.6, delay: 1.2, ease: GLASS.settle }}
            className="font-display m-0 max-w-[11ch] font-bold leading-[0.98] tracking-[-0.035em] text-[#F5F5F7]"
            style={{ fontSize: TYPE.display, textShadow: "0 2px 18px rgba(3, 3, 5,0.7), 0 0 48px rgba(3, 3, 5,0.5)" }}>
            PAINTING WITH LIGHT.
          </motion.h1>
          <motion.p data-testid="window-moment-sub"
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 1.6, delay: 2.4, ease: GLASS.settle }}
            className="mt-7 max-w-[34rem] text-[1.0625rem] font-medium leading-[1.6] text-[#F5F5F7]/90 md:text-lg">
            Engineered optical materials that transform glass, films, and extraordinary surfaces into canvases for projected light.
          </motion.p>
          <motion.p data-testid="window-moment-voice"
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 1.6, delay: 3.0, ease: GLASS.settle }}
            className="font-art mt-6 max-w-md text-[1.4rem] italic leading-[1.3] text-[#D4AF37] md:text-[1.7rem]">
            This is a clear sheet of plastic. Coated, rear-projected, filmed as it happened.{" "}
            <Link to="/lab#lt-003" data-testid="window-moment-record" className="whitespace-nowrap not-italic font-sans text-[0.6em] font-medium uppercase tracking-[0.18em] text-[#84DFF4]/90 underline decoration-[#84DFF4]/30 underline-offset-4 transition-colors hover:text-[#84DFF4]">The record →</Link>
          </motion.p>
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 1.4, delay: 3.2, ease: GLASS.settle }} data-testid="window-moment-doors" className="mt-8 flex flex-wrap items-center gap-x-7 gap-y-2">
            <Link to="/technology" data-testid="window-moment-technology" className={door}>
              <span className={doorWord}>The technology</span>
              <span aria-hidden="true" className={doorArrow}>→</span>
            </Link>
            <button type="button" onClick={openSamples} data-testid="window-moment-samples" className={door}>
              <span className={doorWord}>Hold the material{samplesFrom ? ` · samples from $${samplesFrom}` : " · samples"}</span>
              <span aria-hidden="true" className={doorArrow}>→</span>
            </button>
            <Link to="/collaborate" data-testid="window-moment-contact" className={door}>
              <span className={doorWord}>Contact us</span>
              <span aria-hidden="true" className={doorArrow}>→</span>
            </Link>
          </motion.div>
        </div>
        <motion.div aria-hidden="true" initial={{ opacity: 0 }} animate={{ opacity: playing ? [0, 0.7, 0.2] : 0 }}
          transition={{ duration: 4.2, delay: 3.2, repeat: Infinity, ease: GLASS.refract }} className="mx-auto mt-8 h-7 w-px bg-[#8E8E93]" />
      </div>
    </section>
  );
}
